import { CustomButton } from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import { FunctionComponent, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft } from '@fortawesome/pro-solid-svg-icons';
import { sendCustomEvent } from '@foxtrotplatform/platform-micro-site-core';

import { SignUpStep } from '../../common/provider/SignUpContext';
import { useSignUp } from '../../common/hooks/useSignUp';
import { NavigateToConsoleButton } from './finalize/ConsoleButton';
import { ClearStateBackButton } from './finalize/ClearStateButton';
import { DoneProcessingBackButton } from './finalize/DoneProcessingBackButton';
import { SolutionType } from '../../common/api/types';
import { ApiSolutionButttons } from './finalize/ApiSolutionButtons';

interface ActionBarProps {
  isValid?: boolean;
  onSubmit?: () => void;
  consoleUrl?: string;
}

export const ActionBar: FunctionComponent<ActionBarProps> = ({
  isValid = true,
  onSubmit,
  consoleUrl,
}) => {
  const navigate = useNavigate();
  const { currentStep, selectedSolutionType } = useSignUp();

  const handleGoBackClick = useCallback(() => {
    sendCustomEvent({
      category: SignUpStep[currentStep],
      action: `Back from ${SignUpStep[currentStep]}`,
      label: `User clicked back from ${SignUpStep[currentStep]}`,
      nonInteraction: false,
      href: window.location.href,
    });
    navigate(-1);
  }, [navigate, currentStep]);

  const handleOnsubmitClick = useCallback(() => {
    if (onSubmit) {
      onSubmit();
      if (isValid) {
        sendCustomEvent({
          category: SignUpStep[currentStep],
          action: `Continue from ${SignUpStep[currentStep]}`,
          label: `User clicked continue from ${SignUpStep[currentStep]}`,
          nonInteraction: false,
          href: window.location.href,
        });
      }
    }
  }, [onSubmit, currentStep]);

  const renderBackButton = useCallback(() => {
    switch (currentStep) {
      case SignUpStep.setupFailed:
      case SignUpStep.waitlisted:
      case SignUpStep.contactAdmin:
      case SignUpStep.paymentComplete:
      case SignUpStep.settingUp:
      case SignUpStep.setupSuccess:
        return null;
      default:
        return (
          <CustomButton
            className="px-6"
            actionType="secondary"
            onClick={handleGoBackClick}
            testId="back-btn"
            disabled={false}
          >
            <div className="flex flex-row items-center">
              <FontAwesomeIcon
                icon={faAngleLeft}
                className="h-3 w-3 font-bold"
              />
              <span className="font-bold text-sm">Back</span>
            </div>
          </CustomButton>
        );
    }
  }, [handleGoBackClick, currentStep]);

  const renderActionButton = useCallback(() => {
    switch (currentStep) {
      case SignUpStep.setupSuccess:
        if (selectedSolutionType === SolutionType.API) {
          return <ApiSolutionButttons />;
        }
        return <NavigateToConsoleButton consoleUrl={consoleUrl as string} />;
      case SignUpStep.waitlisted:
        return <ClearStateBackButton />;
      case SignUpStep.contactAdmin:
        return (
          <CustomButton
            actionType="primary"
            onClick={handleGoBackClick}
            testId="waitlist-back-btn"
            className="px-6"
          >
            <span className="font-bold">Go Back</span>
          </CustomButton>
        );
      case SignUpStep.setupFailed:
        return <DoneProcessingBackButton />;
      case SignUpStep.paymentDetails:
      case SignUpStep.paymentComplete:
      case SignUpStep.settingUp:
        return (
          <CustomButton
            className="px-6"
            disabled={!isValid}
            actionType="primary"
            onClick={handleOnsubmitClick}
            testId="submit-btn"
          >
            <div className="flex flex-row items-center">
              <span className="font-bold text-sm">Subscribe</span>
            </div>
          </CustomButton>
        );
      default:
        return (
          <CustomButton
            className="px-6"
            disabled={currentStep === SignUpStep.selectPlan ? !isValid : false}
            actionType="primary"
            onClick={handleOnsubmitClick}
            testId="submit-btn"
          >
            <div className="flex flex-row items-center">
              <span className="font-bold text-sm">Continue</span>
            </div>
          </CustomButton>
        );
    }
  }, [
    consoleUrl,
    handleOnsubmitClick,
    isValid,
    currentStep,
    selectedSolutionType,
    handleGoBackClick,
  ]);

  return (
    <div className="fixed bottom-0 right-0 lg:w-[57%] w-full flex flex-row h-24 bg-primary-100 items-center justify-between p-8">
      <div>{renderBackButton()}</div>
      <div className="flex flex-row gap-x-8">{renderActionButton()}</div>
    </div>
  );
};
